import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useAudience } from '../contexts/AudienceContext';
import { BrandName } from './BrandName';
import SocialMediaLinks from './SocialMediaLinks';

const Footer = () => {
  const whatsappNumber = '+918252113477';
  const { isSchool } = useAudience();

  const handleWhatsApp = () => {
    const text = isSchool
      ? "Hi ChooseRightCareer, I would like to discuss a school partnership or book a demo."
      : "Hi ChooseRightCareer, I'd like to know more about Career DNA for students.";
    window.open(
      `https://wa.me/${whatsappNumber.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`,
      '_blank'
    );
  };

  return (
    <footer className="bg-gray-900 text-white pt-16 pb-20 md:pb-16">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <h3 className="text-2xl mb-4">
              <BrandName variant="footer" className="text-2xl" />
            </h3>
            <p className="text-gray-400 leading-relaxed mb-6">
              {isSchool
                ? 'Career DNA pilots, bulk assessments, and rollout support for Class 9 to 12, built so leadership, counsellors, and families hear the same evidence backed story.'
                : 'Figure out what actually suits you, beyond marks and streams, with Career DNA insights built for students and schools.'}
            </p>
            <Button
              type="button"
              onClick={handleWhatsApp}
              className="bg-green-600 hover:bg-green-700 text-white transition-all duration-300"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Chat on WhatsApp
            </Button>
            <div className="mt-8">
              <p className="mb-3 text-sm font-semibold tracking-wide text-gray-300">Follow us</p>
              <SocialMediaLinks />
            </div>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <a href="#hero" className="text-gray-400 hover:text-white transition-colors duration-300">
                  Home
                </a>
              </li>
              <li>
                <a href="#how-it-works" className="text-gray-400 hover:text-white transition-colors duration-300">
                  How It Works
                </a>
              </li>
              <li>
                <a href="#features" className="text-gray-400 hover:text-white transition-colors duration-300">
                  Features
                </a>
              </li>
              <li>
                <a href="#faq" className="text-gray-400 hover:text-white transition-colors duration-300">
                  FAQ
                </a>
              </li>
              <li>
                {isSchool ? (
                  <Link to="/" className="text-gray-400 hover:text-white transition-colors duration-300">
                    Student experience
                  </Link>
                ) : (
                  <Link to="/schools" className="text-gray-400 hover:text-white transition-colors duration-300">
                    For Schools
                  </Link>
                )}
              </li>
              <li>
                <a href="#student-reviews" className="text-gray-400 hover:text-white transition-colors duration-300">
                  Student reviews
                </a>
              </li>
              <li>
                <Link to="/counsellors" className="text-gray-400 hover:text-white transition-colors duration-300">
                  Expert counsellors
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4">Our Services</h4>
            <ul className="space-y-3 text-gray-400">
              <li>
                <Link to="/start" className="hover:text-white transition-colors duration-300">
                  Career DNA assessment (online)
                </Link>
              </li>
              <li>AI backed reports</li>
              <li>One-to-one sessions</li>
              <li>School partnerships</li>
              <li>Bulk programs</li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                <div>
                  <a
                    href="mailto:info@chooserightcareer.in"
                    className="text-gray-400 hover:text-white transition-colors duration-300"
                  >
                    info@chooserightcareer.in
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                <div>
                  <a href="tel:+918252113477" className="text-gray-400 hover:text-white transition-colors duration-300">
                    +91 82521 13477
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                <div className="text-gray-400">Delhi, India</div>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-gray-800">
          <div className="flex flex-col items-center gap-4 md:flex-row md:flex-wrap md:justify-center md:gap-x-10 md:gap-y-3 px-4">
            <p className="text-gray-400 text-sm text-center md:text-left shrink-0">
              © 2026 ChooseRightCareer. All rights reserved.
            </p>
            <nav
              className="flex flex-wrap justify-center gap-x-5 gap-y-2 md:gap-x-8 text-sm text-gray-400 max-w-full"
              aria-label="Legal"
            >
              <Link to="/privacy-policy" className="hover:text-white transition-colors duration-300">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="hover:text-white transition-colors duration-300">
                Terms of Service
              </Link>
              <Link to="/refund-policy" className="hover:text-white transition-colors duration-300">
                Refund Policy
              </Link>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
